import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isActiveSection = (paths) => paths.some(p => location.pathname.startsWith(p));

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { 
      name: "Services", 
      dropdown: true,
      items: [
        { name: "Consulting", path: "/services/consulting" },
        { name: "In-House Training", path: "/services/in-house-training" },
        { name: "PECB Training", path: "/courses" },
      ]
    },
    { name: "Tools", path: "/tools" },
    { name: "Consultants", path: "/consultants" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
      <nav className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_cert-navigator-2/artifacts/lfp7eysp_Untitled%20design.png" 
              alt="Compliance Management Systems" 
              className="h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              link.dropdown ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                        isActiveSection(['/services', '/courses']) 
                          ? 'text-brand bg-brand/5' 
                          : 'text-slate-700 hover:text-brand hover:bg-slate-50'
                      }`}
                    >
                      {link.name}
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {link.items.map((item) => (
                      <DropdownMenuItem key={item.path} asChild>
                        <Link 
                          to={item.path}
                          className={isActive(item.path) ? 'text-brand font-medium' : ''}
                        >
                          {item.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                    isActive(link.path) 
                      ? 'text-brand bg-brand/5' 
                      : 'text-slate-700 hover:text-brand hover:bg-slate-50'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button className="bg-brand hover:bg-brand-dark text-white">
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100">
            <div className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                link.dropdown ? (
                  <div key={link.name} className="space-y-1">
                    <div className="px-4 py-2 text-sm font-semibold text-slate-500 uppercase tracking-wider">
                      {link.name}
                    </div>
                    {link.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className={`block px-6 py-2 text-sm font-medium transition-colors rounded-lg ${
                          isActive(item.path)
                            ? 'text-brand bg-brand/5'
                            : 'text-slate-700 hover:text-brand hover:bg-slate-50'
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                      isActive(link.path)
                        ? 'text-brand bg-brand/5'
                        : 'text-slate-700 hover:text-brand hover:bg-slate-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <div className="pt-4 px-4">
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-brand hover:bg-brand-dark text-white">
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
